import { useState, useEffect, useMemo } from 'react'
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts'
import { supabase } from '../../lib/supabase'

const DARK_TOOLTIP = {
  backgroundColor: '#1f2937',
  border: '1px solid #374151',
  borderRadius: '8px',
  color: '#fff'
}

export default function DashboardTab() {
  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [analyticsOpen, setAnalyticsOpen] = useState(false)
  const [channelDistOpen, setChannelDistOpen] = useState(false)
  const [statusDistOpen, setStatusDistOpen] = useState(false)

  useEffect(() => {
    loadDashboardData()
  }, [])

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('OTPRequests')
        .select('RequestId, Channel, Status, CreatedUtc, ExpiresUtc, ValidatedUtc, Msisdn, Email')
        .order('CreatedUtc', { ascending: false })
        .limit(5000)

      if (error) throw error
      setRequests(data || [])
    } catch (err) {
      console.error('Failed to load OTP dashboard data:', err)
    } finally {
      setLoading(false)
    }
  }

  const stats = useMemo(() => {
    const now = new Date()
    let validated = 0, pending = 0, expired = 0, failed = 0
    const uniqueTargets = new Set()

    requests.forEach((r) => {
      if (r.Msisdn) uniqueTargets.add(`sms:${r.Msisdn}`)
      if (r.Email) uniqueTargets.add(`email:${r.Email}`)

      const s = String(r.Status || '').toLowerCase()
      const isExpired = new Date(r.ExpiresUtc) < now && s === 'pending'

      if (s === 'validated') validated++
      else if (s === 'failed' || s === 'invalid') failed++
      else if (isExpired) expired++
      else if (s === 'pending') pending++
    })

    return {
      total: requests.length,
      validated,
      pending,
      expired,
      failed,
      uniqueTargets: uniqueTargets.size,
    }
  }, [requests])

  const dailyData = useMemo(() => {
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - 29)
    const dayMap = {}
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const key = d.toISOString().slice(0, 10)
      dayMap[key] = { date: key, issued: 0, validated: 0 }
    }
    requests.forEach((r) => {
      const issuedDay = new Date(r.CreatedUtc).toISOString().slice(0, 10)
      if (dayMap[issuedDay]) dayMap[issuedDay].issued++
      if (r.ValidatedUtc) {
        const vDay = new Date(r.ValidatedUtc).toISOString().slice(0, 10)
        if (dayMap[vDay]) dayMap[vDay].validated++
      }
    })
    return Object.values(dayMap)
  }, [requests])

  const channelDistData = useMemo(() => {
    const counts = {}
    requests.forEach((r) => {
      const c = String(r.Channel || 'Unknown')
      counts[c] = (counts[c] || 0) + 1
    })
    const palette = { sms: '#3b82f6', email: '#a855f7', whatsapp: '#22c55e' }
    return Object.entries(counts).map(([name, value]) => ({
      name,
      value,
      color: palette[name.toLowerCase()] || '#6b7280'
    }))
  }, [requests])

  const statusDistData = useMemo(() => [
    { name: 'Validated', value: stats.validated, color: '#22c55e' },
    { name: 'Pending', value: stats.pending, color: '#eab308' },
    { name: 'Expired', value: stats.expired, color: '#f97316' },
    { name: 'Failed', value: stats.failed, color: '#ef4444' },
  ].filter((d) => d.value > 0), [stats])

  const getPercentage = (value, total) => {
    if (!total) return 0
    return Math.round((value / total) * 100)
  }

  const recent = useMemo(() => requests.slice(0, 10), [requests])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">OTP Service Dashboard</h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Overview of OTP issuance and validation</p>
        </div>
        <button
          onClick={loadDashboardData}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition flex items-center space-x-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <span>Refresh</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard title="Total Requests" value={stats.total} iconColor="bg-blue-500" tooltip="Total OTP requests issued">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </StatCard>

        <StatCard
          title="Validated"
          value={stats.validated}
          sub={`${getPercentage(stats.validated, stats.total)}% validation rate`}
          iconColor="bg-green-500"
          tooltip="OTP requests successfully validated by the user"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </StatCard>

        <StatCard
          title="Pending"
          value={stats.pending}
          sub="Awaiting validation"
          iconColor="bg-yellow-500"
          tooltip="Requests still within their expiry window and not yet validated"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </StatCard>

        <StatCard
          title="Expired"
          value={stats.expired}
          sub={`${getPercentage(stats.expired, stats.total)}% expired`}
          iconColor="bg-orange-500"
          tooltip="Requests that timed out without validation"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </StatCard>

        <StatCard
          title="Failed"
          value={stats.failed}
          sub={`${getPercentage(stats.failed, stats.total)}% failure rate`}
          iconColor="bg-red-500"
          tooltip="Requests that failed (wrong code, rejected, etc.)"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </StatCard>

        <StatCard
          title="Unique Targets"
          value={stats.uniqueTargets}
          sub="Distinct MSISDN + email"
          iconColor="bg-teal-500"
          tooltip="Count of distinct phone numbers and emails that received at least one OTP"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </StatCard>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setAnalyticsOpen(!analyticsOpen)}
          className="w-full px-6 py-4 flex items-center justify-between text-left border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
        >
          <div className="flex items-center space-x-3">
            <svg className="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Performance Analytics</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">30-day issuance + channel / status breakdown</p>
            </div>
          </div>
          <svg
            className={`w-5 h-5 text-gray-500 transition-transform ${analyticsOpen ? 'rotate-180' : ''}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {analyticsOpen && (
          <div className="p-6 space-y-4">
            <div className="mb-6">
              <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-2">Daily OTP Volume</h4>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Issued vs validated over the last 30 days</p>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={dailyData}>
                    <defs>
                      <linearGradient id="otpIssued" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="otpValidated" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                    <XAxis dataKey="date" tick={{ fill: '#9ca3af', fontSize: 12 }} interval="preserveStartEnd" />
                    <YAxis tick={{ fill: '#9ca3af', fontSize: 12 }} />
                    <Tooltip contentStyle={DARK_TOOLTIP} />
                    <Legend />
                    <Area type="monotone" dataKey="issued" name="Issued" stroke="#3b82f6" fillOpacity={1} fill="url(#otpIssued)" />
                    <Area type="monotone" dataKey="validated" name="Validated" stroke="#22c55e" fillOpacity={1} fill="url(#otpValidated)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <SubAccordion title="Channel Distribution" open={channelDistOpen} onToggle={() => setChannelDistOpen(!channelDistOpen)}>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={channelDistData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                    <XAxis dataKey="name" tick={{ fill: '#9ca3af', fontSize: 11 }} />
                    <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
                    <Tooltip contentStyle={DARK_TOOLTIP} />
                    <Bar dataKey="value" name="Requests" radius={[4, 4, 0, 0]}>
                      {channelDistData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </SubAccordion>

            <SubAccordion title="Status Distribution" open={statusDistOpen} onToggle={() => setStatusDistOpen(!statusDistOpen)}>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusDistData}
                      cx="50%" cy="50%" innerRadius={60} outerRadius={100}
                      paddingAngle={2} dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={{ stroke: '#9ca3af' }}
                    >
                      {statusDistData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={DARK_TOOLTIP} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </SubAccordion>
          </div>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Activity</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Last 10 OTP requests</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Channel</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Target</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {recent.length > 0 ? (
                recent.map((r) => (
                  <tr key={r.RequestId} className="hover:bg-gray-50 dark:hover:bg-gray-900 transition">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{r.Channel}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900 dark:text-white">
                      {r.Msisdn || r.Email || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 capitalize">{r.Status}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {new Date(r.CreatedUtc).toLocaleString()}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    No recent activity
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function StatCard({ title, value, sub, iconColor, tooltip, children }) {
  return (
    <div
      className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700 cursor-help transition hover:shadow-lg"
      title={tooltip}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</p>
          <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{value}</p>
          {sub && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{sub}</p>}
        </div>
        <div className={`${iconColor} p-3 rounded-lg text-white`}>
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {children}
          </svg>
        </div>
      </div>
    </div>
  )
}

function SubAccordion({ title, open, onToggle, children }) {
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between text-left bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
      >
        <span className="font-medium text-gray-900 dark:text-white">{title}</span>
        <svg
          className={`w-4 h-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && <div className="p-4 bg-white dark:bg-gray-800">{children}</div>}
    </div>
  )
}
